import { m, verbatim, type Message, type PlainKey } from '@/i18n';
import type { Origin, StoredOrigin } from './types';

/**
 * **The stored English, read back as descriptors** (docs/I18N.md). Until
 * 2026-10-03 a plan's statement was an English sentence, and the log stored it
 * as one: `events.origin.statement`, `proposal_changes.statement`, and the
 * `statement` of the proposal events. Since then a statement is a `Message`
 * (a key plus its arguments), and the log stores that. Rows already written
 * keep their sentence; there is no data migration. Every reader of a stored
 * statement goes through `readStatement`, which accepts both shapes.
 *
 * This file is the one place that knows the old English. It recognises every
 * sentence the old producers could emit (the resolver from 2026-09-21 on, and
 * the tag rename's own plan), and returns the matching descriptor, so a
 * sentence stored in English reads in the viewer's language. A sentence it
 * does not recognise is shown as stored (`verbatim`).
 *
 * **Delete this file**, together with the `legacy` catalog area
 * (src/i18n/messages/en/legacy.ts and its German twin), once no stored row
 * predates 2026-10-03.
 */

/** A stored statement, either shape, as a descriptor; null for none. */
export function readStatement(stored: unknown): Message | null {
  if (typeof stored === 'string') return parseLegacyStatement(stored);
  if (isStoredMessage(stored)) return stored;
  return null;
}

/** An origin marker as the log holds it, with its statement as a descriptor. */
export function readOrigin(stored: StoredOrigin): Origin {
  return { ...stored, statement: readStatement(stored.statement) ?? verbatim('') };
}

function isStoredMessage(value: unknown): value is Message {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { key?: unknown }).key === 'string'
  );
}

/** The sentences with no argument, each with the key that now says it. */
const FIXED: Readonly<Record<string, PlainKey>> = {
  'Delivered.': 'resolution.statement.delivered',
  'Closed.': 'resolution.statement.closed',
  'Delivered and closed.': 'resolution.statement.deliveredAndClosed',
  'Accepted.': 'resolution.statement.accepted',
  'Denied.': 'resolution.statement.denied',
  'Verdict withdrawn.': 'resolution.statement.verdictWithdrawn',
  'Delivery taken back.': 'resolution.statement.deliveryTakenBack',
  'Reopened.': 'resolution.statement.reopened',
  'Cancelled.': 'resolution.statement.cancelled',
  'Taken back.': 'resolution.statement.takenBack',
  'Seated as doer.': 'resolution.statement.seated',
  'Seated as doer; closed.': 'resolution.statement.seatedClosed',
  'Seated as doer; delivered and closed.': 'resolution.statement.seatedDeliveredAndClosed',
  'Delivered; waits on the review.': 'resolution.statement.deliveredWaitsOnReview',
  'Seated as doer; waits on the review.': 'resolution.statement.seatedWaitsOnReview',
  'Seated as doer and delivered; waits on the review.':
    'resolution.statement.seatedDeliveredWaitsOnReview',
  'Marked active.': 'resolution.statement.markedActive',
  'Marked idle.': 'resolution.statement.markedIdle',
  'Commented.': 'resolution.statement.commented',
  'Acknowledged.': 'resolution.statement.acknowledged',
  'Owner set.': 'resolution.statement.ownerSet',
  'Doer set.': 'resolution.statement.doerSet',
  'Changed.': 'resolution.statement.changed',
  'Renamed.': 'resolution.statement.renamed',
  // Produced until 2026-09-22 (the deferred close, and a doer's tick that
  // waited on the requester's close).
  "Delivered; waits on the requester's close.": 'legacy.statement.deliveredWaitsOnRequester',
  "Seated as doer and delivered; waits on the requester's close.":
    'legacy.statement.seatedDeliveredWaitsOnRequester',
  'Delivered; closes when the review lands.': 'legacy.statement.deliveredClosesOnReview',
  'The sub-tasks are done: closing, as deferred.': 'legacy.statement.subTasksDoneClosing',
  'The review landed: closing, as deferred.': 'legacy.statement.reviewLandedClosing',
};

/** The sentences with arguments: a pattern over the old template, and the
 *  descriptor its groups fill. Tag paths hold no whitespace (`parseTagPath`). */
const PATTERNS: readonly {
  pattern: RegExp;
  read: (groups: RegExpMatchArray) => Message;
}[] = [
  {
    pattern: /^Renamed; tagged (#\S+(?: #\S+)*)\.$/,
    read: (g) => m('resolution.statement.renamedAndTagged', { tags: g[1]! }),
  },
  {
    pattern: /^Tagged (#\S+(?: #\S+)*)\.$/,
    read: (g) => m('resolution.statement.tagged', { tags: g[1]! }),
  },
  {
    pattern: /^Removed #(\S+)\.$/,
    read: (g) => m('resolution.statement.untagged', { tag: g[1]! }),
  },
  {
    pattern: /^Changed #(\S+) to #(\S+)\.$/,
    read: (g) => m('resolution.statement.retagged', { from: g[1]!, to: g[2]! }),
  },
  {
    pattern: /^Renamed #(\S+) to #(\S+) on (\d+) items?\.$/,
    read: (g) =>
      m('resolution.statement.tagRenamed', { from: g[1]!, to: g[2]!, count: Number(g[3]) }),
  },
  {
    // A stage label is the flow author's text, so it may hold anything,
    // quotes included: the last `".` closes it.
    pattern: /^Moved to "(.*)"\.$/s,
    read: (g) => m('resolution.statement.moved', { stage: g[1]! }),
  },
  {
    pattern: /^Delivered; closes when the (\d+) sub-tasks? closes?\.$/,
    read: (g) => m('legacy.statement.deliveredClosesWithSubTasks', { count: Number(g[1]) }),
  },
];

/** Until 2026-09-21 a conclusion that left locked sub-tasks open appended
 *  their count to its statement: `"Closed. 2 stays open."`. */
const LOCKED_SUFFIX = /^(.+) (\d+) stays open\.$/s;

/** One stored English sentence as a descriptor; `verbatim` where it is not a
 *  sentence the old producers emitted. */
export function parseLegacyStatement(sentence: string): Message {
  return recognise(sentence) ?? verbatim(sentence);
}

function recognise(sentence: string): Message | null {
  const fixed = FIXED[sentence];
  if (fixed) return m(fixed);
  for (const { pattern, read } of PATTERNS) {
    const match = sentence.match(pattern);
    if (match) return read(match);
  }
  const suffixed = sentence.match(LOCKED_SUFFIX);
  if (suffixed) {
    const inner = recognise(suffixed[1]!);
    if (inner) {
      return m('legacy.statement.withLockedStaying', {
        statement: inner,
        count: Number(suffixed[2]),
      });
    }
  }
  return null;
}
